import { Heading, Text } from '@chakra-ui/react';
import { NextSeo } from 'next-seo';
import { Fragment } from 'react';

import { Content } from '@/components/templatePage/content';
import { Header } from '@/components/templatePage/header';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';

const PrivacyPolicy = () => {
  return (
    <Fragment>
      <NextSeo
        title="Privacy Policy | Underline Events and Underline Digital Video Library"
        titleTemplate="%s"
        description="Privacy Policy for Underline Science, including access to Underline Events, scientific conference services, hybrid events, virtual events, and the Underline Digital Video Library."
        openGraph={{
          description:
            'Privacy Policy for Underline Science, including access to Underline Events, scientific conference services, hybrid events, virtual events, and the Underline Digital Video Library.',
          url: `${process.env.NEXT_PUBLIC_BASE_URL}/privacy-policy`,
        }}
      />
      <Layout>
        <Header>
          Underline Science, Inc.
          <br />
          Privacy Policy
        </Header>
        <Content>
          <Text>
            <Text as="span" fontWeight="bold">
              Effective Date:{' '}
            </Text>
            May 1, 2026
            <br />
            <Text as="span" fontWeight="bold">
              Last Updated:{' '}
            </Text>
            April 28, 2026
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            1. Introduction
          </Heading>
          <Text>
            This Privacy Policy explains how Underline Science, Inc. and its affiliates, service providers, and
            authorized partners collect, use, disclose, retain, and protect personal information when you use our
            websites, platforms, event portals, conference services, video library, livestreaming services, digital
            tools, and related services.
          </Text>
          <Text as="span">This Privacy Policy applies to:</Text>
          <Text as="ol" listStyleType="decimal" ps={6}>
            <li>
              <Text as="span" fontWeight="bold">
                Underline Events
              </Text>
              , including scientific conferences, academic events, medical meetings, hybrid events, virtual events,
              in-person event services, livestreaming, session recordings, event websites, registration tools, attendee
              tools, speaker tools, exhibitor tools, help desk services, and related conference services; and
            </li>
            <li>
              <Text as="span" fontWeight="bold">
                Underline Digital Video Library
              </Text>
              , including access to scientific videos, academic lectures, medical conference videos, research
              presentations, posters, slides, transcripts, abstracts, proceedings, metadata, search tools,
              recommendation tools, citation tools, institutional access, and related digital library services.
            </li>
          </Text>
          <Text>
            This Privacy Policy does not replace any written agreement between Underline and a customer, society,
            publisher, institution, library, event organizer, sponsor, exhibitor, or other contracting party. If a
            separate written agreement applies to specific services, that agreement may contain additional privacy,
            security, access, data-processing, or confidentiality terms.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            2. Who We Are
          </Heading>
          <Text>
            <Text as="span" fontWeight="bold">
              Underline Science, Inc.
            </Text>
            <br />
            1216 Broadway, 2nd Floor
            <br />
            New York, NY 10001
            <br />
            United States
          </Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Privacy contact:
            </Text>{' '}
            support@underline.io
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            3. Personal Information We Collect
          </Heading>
          <Text>
            We collect personal information directly from you, from customers and event organizers, from institutions
            and access providers, from speakers and presenters, from payment and registration systems, from your device
            and browser, and from your use of our services.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            3.1 Account and Contact Information
          </Heading>
          <Text as="span">We may collect:</Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>name;</li>
            <li>email address;</li>
            <li>phone number;</li>
            <li>organization, institution, company, society, or publisher affiliation;</li>
            <li>job title or role;</li>
            <li>username, password, and account credentials;</li>
            <li>profile information;</li>
            <li>communication preferences;</li>
            <li>country, region, or time zone;</li>
            <li>messages you send to us.</li>
          </Text>
          <Text>
            We use this information to create accounts, authenticate users, provide support, respond to inquiries,
            manage event and library access, send service communications, and communicate about Underline services.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            3.2 Event Registration and Attendance Information
          </Heading>
          <Text as="span">For Underline Events, we may collect:</Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>event registration details;</li>
            <li>attendee name, email, affiliation, and role;</li>
            <li>badge or ticket information;</li>
            <li>session selections;</li>
            <li>payment or invoice status;</li>
            <li>dietary, accessibility, or accommodation requests where applicable;</li>
            <li>speaker, moderator, panelist, or author information;</li>
            <li>sponsor, exhibitor, or organizer contact information;</li>
            <li>attendance records;</li>
            <li>session participation data;</li>
            <li>questions, chat messages, polls, survey responses, help desk messages, and feedback.</li>
          </Text>
          <Text>
            We use this information to operate scientific conferences, academic events, medical meetings, hybrid events,
            virtual events, in-person events, livestreams, recordings, attendee tools, speaker tools, schedules, help
            desks, and post-event access.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            3.3 Speaker, Presenter, and Contributor Information
          </Heading>
          <Text as="span">
            If you are a speaker, presenter, moderator, panelist, author, poster presenter, exhibitor, sponsor, or
            contributor, we may collect:
          </Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>name;</li>
            <li>title;</li>
            <li>affiliation;</li>
            <li>biography;</li>
            <li>headshot or profile image;</li>
            <li>email address;</li>
            <li>presentation title;</li>
            <li>abstract;</li>
            <li>paper, poster, slide deck, or supporting files;</li>
            <li>voice, image, likeness, and presentation captured in livestreams or recordings;</li>
            <li>transcript, caption, translation, and metadata generated from your presentation;</li>
            <li>consent, release, or rights-management information.</li>
          </Text>
          <Text>
            We use this information to support event production, livestreaming, recording, publication, search,
            citation, accessibility, archiving, and discovery of scientific and academic content.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            3.4 Digital Video Library Usage Information
          </Heading>
          <Text as="span">For the Underline Digital Video Library, we may collect:</Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>account and institutional access status;</li>
            <li>IP address or SSO authentication information;</li>
            <li>videos viewed;</li>
            <li>search queries;</li>
            <li>clicked results;</li>
            <li>watch time;</li>
            <li>transcript usage;</li>
            <li>downloads where permitted;</li>
            <li>bookmarks, saved items, playlists, citations, or recommendations;</li>
            <li>DOI, metadata, MARC, or catalog-related interactions;</li>
            <li>library entitlement and access logs;</li>
            <li>device and browser information.</li>
          </Text>
          <Text>
            We use this information to provide access, enforce entitlements, improve search and recommendations, support
            institutional reporting, troubleshoot technical issues, measure content engagement, and improve the digital
            video library.
          </Text>
          <Text>
            Because Underline provides video content, viewing-history and video-engagement analytics should be handled
            carefully. Counsel should specifically review use of marketing pixels, analytics tools, and disclosure of
            video-viewing data to third parties because U.S. litigation has treated video-viewing data as sensitive
            under the Video Privacy Protection Act in some online-video contexts.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            3.5 Payment and Billing Information
          </Heading>
          <Text as="span">
            If you purchase services, register for a paid event, subscribe, license content, or pay an invoice, we may
            collect:
          </Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>billing name;</li>
            <li>billing email;</li>
            <li>billing address;</li>
            <li>organization name;</li>
            <li>invoice details;</li>
            <li>purchase history;</li>
            <li>payment status;</li>
            <li>tax information;</li>
            <li>transaction identifiers.</li>
          </Text>
          <Text>
            Payments may be processed by third-party payment processors. Underline does not need to store full credit
            card numbers when payments are processed by third-party payment providers.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            3.6 Technical, Device, and Log Information
          </Heading>
          <Text as="span">We may automatically collect:</Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>IP address;</li>
            <li>browser type;</li>
            <li>operating system;</li>
            <li>device type;</li>
            <li>referring and exit pages;</li>
            <li>pages visited;</li>
            <li>links clicked;</li>
            <li>search terms;</li>
            <li>session duration;</li>
            <li>event logs;</li>
            <li>error logs;</li>
            <li>approximate location derived from IP address;</li>
            <li>authentication logs;</li>
            <li>security logs;</li>
            <li>cookie identifiers;</li>
            <li>analytics identifiers.</li>
          </Text>
          <Text>
            We use this information for security, authentication, access control, analytics, fraud prevention,
            troubleshooting, platform performance, user experience, and service improvement.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            3.7 Cookies and Similar Technologies
          </Heading>
          <Text as="span">
            We may use cookies, pixels, tags, local storage, SDKs, analytics tools, and similar technologies to:
          </Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>keep users logged in;</li>
            <li>remember preferences;</li>
            <li>provide event and library access;</li>
            <li>measure website and platform usage;</li>
            <li>analyze content engagement;</li>
            <li>improve search and recommendations;</li>
            <li>support marketing and campaign measurement;</li>
            <li>prevent fraud and abuse;</li>
            <li>secure the services.</li>
          </Text>
          <Text>
            Some cookies are necessary for the services to function. Others may be used for analytics, personalization,
            or marketing, depending on your settings and applicable law.
          </Text>
          <Text>
            Where required, we will request consent before placing non-essential cookies or similar technologies.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            3.8 AI Search, Recommendation, Transcript, and Discovery Data
          </Heading>
          <Text>
            Underline may provide AI-powered or machine-assisted features, including search, recommendations,
            transcripts, captions, translations, summaries, metadata enrichment, question-answering, and content
            discovery.
          </Text>
          <Text as="span">When you use these features, we may process:</Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>search queries;</li>
            <li>prompts or questions;</li>
            <li>clicked results;</li>
            <li>transcripts and metadata;</li>
            <li>content snippets;</li>
            <li>video engagement signals;</li>
            <li>user feedback;</li>
            <li>system-generated recommendations, summaries, or outputs.</li>
          </Text>
          <Text>
            We use this information to provide and improve discovery, accessibility, search relevance, recommendations,
            and platform functionality.
          </Text>
          <Text>
            We do not intend for users to submit sensitive personal information, patient data, protected health
            information, confidential research data, unpublished proprietary data, or regulated personal data in AI
            queries or prompts.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            4. Sources of Personal Information
          </Heading>
          <Text as="span">We may collect personal information from:</Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>you directly;</li>
            <li>your organization, institution, employer, library, society, publisher, or event organizer;</li>
            <li>conference registration systems;</li>
            <li>event sponsors, exhibitors, or partners, where applicable;</li>
            <li>speakers, presenters, authors, and moderators;</li>
            <li>payment processors;</li>
            <li>
              authentication providers, including SSO, domain-based access, IP authentication, or identity providers;
            </li>
            <li>analytics and security tools;</li>
            <li>customer relationship management and marketing systems;</li>
            <li>
              publicly available sources, such as conference programs, speaker pages, institutional directories,
              publications, or professional profiles;
            </li>
            <li>service providers that support our platform.</li>
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            5. How We Use Personal Information
          </Heading>
          <Text as="span">We use personal information to:</Text>
          <Text as="ol" listStyleType="decimal" ps={6}>
            <li>provide, operate, maintain, and improve the services;</li>
            <li>
              register users for scientific conferences, academic events, medical events, hybrid events, virtual events,
              and in-person events;
            </li>
            <li>authenticate users and manage accounts;</li>
            <li>provide institutional, subscription, event, or role-based access;</li>
            <li>
              support livestreaming, recording, archiving, transcription, captioning, translation, search, and
              publication of conference content;
            </li>
            <li>operate the Underline Digital Video Library;</li>
            <li>provide customer support and help desk services;</li>
            <li>process payments, invoices, subscriptions, licenses, and event registrations;</li>
            <li>
              send transactional messages, service notices, event updates, access instructions, and support
              communications;
            </li>
            <li>send marketing communications where permitted by law;</li>
            <li>
              provide analytics to customers, societies, publishers, institutions, libraries, sponsors, exhibitors, and
              event organizers;
            </li>
            <li>improve search, recommendations, accessibility, metadata, and content discovery;</li>
            <li>detect, prevent, and respond to fraud, abuse, security incidents, unauthorized access, and misuse;</li>
            <li>enforce our Terms of Use, customer agreements, event rules, and access restrictions;</li>
            <li>comply with legal, regulatory, tax, accounting, and contractual obligations;</li>
            <li>
              protect the rights, safety, property, and security of Underline, our users, customers, partners, and the
              public;
            </li>
            <li>
              support business transactions, including financing, merger, acquisition, sale, diligence, reorganization,
              or transfer of assets.
            </li>
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            6. Legal Bases for Processing for EEA, UK, and Similar Jurisdictions
          </Heading>
          <Text as="span">
            Where GDPR, UK GDPR, or similar laws apply, we process personal information under one or more of the
            following legal bases:
          </Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>Contract: to provide services requested by you or by a customer with whom you are affiliated;</li>
            <li>
              Legitimate interests: to operate, secure, improve, and market our services; support customers; prevent
              fraud; provide analytics; and manage business operations;
            </li>
            <li>
              Consent: where required for certain cookies, marketing communications, recording permissions, or optional
              features;
            </li>
            <li>
              Legal obligation: to comply with applicable laws, tax, accounting, regulatory, or legal-process
              obligations;
            </li>
            <li>Vital or public interest: only where applicable and legally justified.</li>
          </Text>
          <Text>
            The GDPR has applied in EU member states since May 25, 2018, and requires transparency about personal data
            processing, including purposes, legal bases, and data-subject rights.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            7. How We Disclose Personal Information
          </Heading>
          <Text>We may disclose personal information to the following categories of recipients.</Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            7.1 Service Providers and Contractors
          </Heading>
          <Text as="span">We may disclose personal information to vendors that provide:</Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>cloud hosting;</li>
            <li>video storage;</li>
            <li>streaming and content delivery;</li>
            <li>video transcoding;</li>
            <li>transcription, captioning, and translation;</li>
            <li>analytics;</li>
            <li>authentication;</li>
            <li>registration;</li>
            <li>payment processing;</li>
            <li>email delivery;</li>
            <li>customer support;</li>
            <li>CRM;</li>
            <li>marketing tools;</li>
            <li>security monitoring;</li>
            <li>data backup;</li>
            <li>database management;</li>
            <li>professional services;</li>
            <li>legal, accounting, and compliance support.</li>
          </Text>
          <Text>
            These providers may process personal information only as needed to provide services to Underline, subject to
            applicable contractual obligations.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            7.2 Customers, Event Organizers, Societies, Publishers, and Institutions
          </Heading>
          <Text as="span">
            We may disclose relevant personal information to the customer or organization responsible for the event,
            subscription, library license, or content collection, including:
          </Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>event organizers;</li>
            <li>scientific societies;</li>
            <li>academic societies;</li>
            <li>medical societies;</li>
            <li>publishers;</li>
            <li>libraries;</li>
            <li>universities;</li>
            <li>research institutions;</li>
            <li>sponsors or exhibitors where applicable;</li>
            <li>institutional administrators.</li>
          </Text>
          <Text>
            Examples include registration lists, attendance reports, speaker information, session participation, access
            logs, usage analytics, content engagement, support requests, and post-event analytics.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            7.3 Speakers, Attendees, Sponsors, and Exhibitors
          </Heading>
          <Text as="span">
            Depending on event features and event rules, certain information may be visible to other event participants,
            including:
          </Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>name;</li>
            <li>affiliation;</li>
            <li>profile information;</li>
            <li>submitted questions;</li>
            <li>chat messages;</li>
            <li>poster or presentation materials;</li>
            <li>sponsor or exhibitor interactions;</li>
            <li>networking information you choose to share.</li>
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            7.4 Institutional Analytics and Library Reporting
          </Heading>
          <Text>
            For institutional subscriptions and library access, we may provide aggregated, de-identified, or user-level
            reporting where contractually permitted. Reports may include usage volume, content engagement, access
            method, search activity, viewed content, and subscription performance.
          </Text>
          <Text>
            Where possible, we use aggregated or de-identified reporting. User-level reporting may be provided when
            required to operate the service, enforce access rights, provide customer support, comply with institutional
            license terms, or where otherwise permitted by law and contract.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            7.5 Legal, Safety, and Compliance Disclosures
          </Heading>
          <Text as="span">We may disclose personal information if we believe disclosure is necessary to:</Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>comply with law, legal process, subpoena, court order, or government request;</li>
            <li>enforce our Terms of Use or agreements;</li>
            <li>protect rights, property, safety, or security;</li>
            <li>investigate fraud, abuse, unauthorized access, or security incidents;</li>
            <li>respond to claims that content violates third-party rights;</li>
            <li>protect users, customers, speakers, or the public.</li>
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            7.6 Business Transfers
          </Heading>
          <Text>
            If Underline is involved in a merger, acquisition, financing, reorganization, bankruptcy, sale of assets,
            diligence process, or similar transaction, personal information may be disclosed or transferred as part of
            that transaction, subject to appropriate confidentiality and legal protections.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            8. Sale or Sharing of Personal Information
          </Heading>
          <Text>Underline does not sell personal information for money.</Text>
          <Text>
            Some privacy laws define &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; broadly to include certain disclosures
            for targeted advertising, cross-context behavioral advertising, or marketing analytics. If Underline uses
            advertising cookies, pixels, or similar tools, those activities may be considered &ldquo;sharing&rdquo;
            under California law or similar laws.
          </Text>
          <Text>
            Where required, we will provide a way to opt out of sale or sharing, including through a Do Not Sell or
            Share My Personal Information link or other legally required mechanism.
          </Text>
          <Text>
            California privacy law gives consumers rights to know, delete, opt out of sale or sharing, avoid
            discrimination, correct inaccurate information, and limit certain uses of sensitive personal information.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            9. Sensitive Personal Information
          </Heading>
          <Text>
            We do not intentionally collect sensitive personal information unless it is necessary for a specific
            service, provided by you, required by law, or collected with appropriate authorization.
          </Text>
          <Text>
            Sensitive personal information may include accessibility requests, dietary restrictions, account login
            credentials, payment-related information, government identifiers, precise geolocation, health information,
            or other information treated as sensitive under applicable law.
          </Text>
          <Text>
            You should not submit patient information, protected health information, confidential clinical information,
            unpublished sensitive research data, government identifiers, or other sensitive personal information through
            Underline unless specifically authorized and covered by an applicable written agreement.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            10. Children&apos;s Privacy
          </Heading>
          <Text>
            The services are not directed to children under 13, and we do not knowingly collect personal information
            from children under 13.
          </Text>
          <Text>
            If we learn that we have collected personal information from a child under 13 without appropriate consent,
            we will take reasonable steps to delete it.
          </Text>
          <Text>
            Some scientific, academic, or educational events may involve students or younger participants. In those
            cases, the event organizer, institution, parent, guardian, or school is responsible for obtaining any
            legally required consent unless otherwise stated in a written agreement.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            11. International Data Transfers
          </Heading>
          <Text>
            Underline is based in the United States, and personal information may be processed in the United States and
            other countries where we, our customers, or our service providers operate.
          </Text>
          <Text>
            If we transfer personal information from the EEA, UK, Switzerland, or another jurisdiction with cross-border
            transfer rules, we will use appropriate safeguards where required, such as contractual protections, standard
            contractual clauses, or other lawful transfer mechanisms.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            12. Data Retention
          </Heading>
          <Text>
            We retain personal information for as long as reasonably necessary to provide the services, operate events,
            maintain accounts, support library access, preserve scientific content, comply with legal obligations,
            resolve disputes, enforce agreements, maintain security, and support legitimate business purposes.
          </Text>
          <Text as="span">Retention periods may vary by category:</Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>account information: while the account remains active and for a reasonable period afterward;</li>
            <li>
              event registration records: as needed for event operations, reporting, accounting, and customer
              requirements;
            </li>
            <li>
              speaker and presentation metadata: as needed to support citation, discovery, archival, library, DOI, and
              scholarly-record purposes;
            </li>
            <li>
              video recordings, transcripts, slides, posters, proceedings, and related content: according to customer
              agreements, speaker permissions, library licenses, publication rules, and archival requirements;
            </li>
            <li>payment and billing records: as required for tax, accounting, audit, and legal purposes;</li>
            <li>security logs: as needed for fraud prevention, incident response, and platform security;</li>
            <li>
              marketing records: until you unsubscribe or we no longer need them for legitimate business purposes.
            </li>
          </Text>
          <Text>Where required by law, we will disclose more specific retention periods or retention criteria.</Text>
          <Text>
            California privacy regulations require notices to identify categories of personal information, sources,
            purposes, disclosures, sale/sharing status, rights, and retention periods or retention criteria.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            13. Security
          </Heading>
          <Text>
            We use reasonable technical, organizational, and administrative safeguards designed to protect personal
            information against unauthorized access, disclosure, alteration, loss, misuse, and destruction.
          </Text>
          <Text>
            These safeguards may include access controls, authentication, encryption, monitoring, logging, vendor
            controls, backups, and internal security procedures.
          </Text>
          <Text>
            No system is completely secure. We cannot guarantee that personal information will never be accessed,
            disclosed, altered, or destroyed by unauthorized persons.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            14. Your Choices and Rights
          </Heading>
          <Text as="span">Depending on where you live and how you use the services, you may have rights to:</Text>
          <Text as="ul" listStyleType="disc" ps={6}>
            <li>access personal information;</li>
            <li>correct inaccurate personal information;</li>
            <li>delete personal information;</li>
            <li>restrict processing;</li>
            <li>object to processing;</li>
            <li>withdraw consent;</li>
            <li>opt out of marketing communications;</li>
            <li>opt out of sale or sharing where applicable;</li>
            <li>limit use of sensitive personal information where applicable;</li>
            <li>receive a portable copy of personal information;</li>
            <li>appeal a denied privacy request where applicable;</li>
            <li>lodge a complaint with a data protection authority.</li>
          </Text>
          <Text>To exercise privacy rights, contact us at:</Text>
          <Text>
            support@underline.io
            <br />
            Subject line: Privacy Request
          </Text>
          <Text>
            We may need to verify your identity before fulfilling your request. We may also deny or limit requests where
            permitted by law, including where we must retain information for security, legal, contractual, archival,
            research-integrity, citation, fraud-prevention, accounting, or operational reasons.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            15. Marketing Communications
          </Heading>
          <Text>
            You may opt out of marketing emails by clicking the unsubscribe link in our emails or contacting us at
            <Text as="span" fontWeight="bold">
              {' '}
              support@underline.io.
            </Text>
          </Text>
          <Text>
            Even if you opt out of marketing messages, we may still send transactional or service-related
            communications, including account notices, event access information, registration confirmations, payment
            notices, security alerts, policy updates, and support messages.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            16. Cookies and Analytics Choices
          </Heading>
          <Text>
            You can control cookies through your browser settings. Some browsers also offer global privacy controls or
            opt-out preference signals.
          </Text>
          <Text>
            If you disable cookies, some features may not work properly, including account login, event access,
            institutional access, video playback, entitlements, saved preferences, or analytics-dependent features.
          </Text>
          <Text>
            Where required, we will honor applicable consent choices and legally recognized opt-out preference signals.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            17. Third-Party Websites and Services
          </Heading>
          <Text>
            The services may link to third-party websites, platforms, tools, sponsors, exhibitors, publishers,
            societies, payment processors, conference tools, social media pages, or external content.
          </Text>
          <Text>
            This Privacy Policy does not apply to third-party websites or services. We are not responsible for the
            privacy practices of third parties. Review their privacy policies before submitting personal information to
            them.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            18. Public and Published Content
          </Heading>
          <Text>
            Some content submitted through Underline may be public or published, including speaker names, affiliations,
            presentation titles, abstracts, slides, posters, papers, proceedings, recordings, transcripts, captions,
            metadata, DOI records, and conference pages.
          </Text>
          <Text>
            If your content is public, published, archived, cited, indexed, or distributed through a customer, society,
            publisher, library, DOI system, search engine, or academic catalog, it may not be possible to fully remove
            all copies from third-party systems.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            19. Changes to This Privacy Policy
          </Heading>
          <Text>We may update this Privacy Policy from time to time.</Text>
          <Text>
            When we make material changes, we will update the &ldquo;Last Updated&rdquo; date and may provide additional
            notice where required by law.
          </Text>
          <Text>
            Your continued use of the services after an updated Privacy Policy becomes effective means the updated
            Privacy Policy applies to future processing of personal information.
          </Text>

          <Heading as="h3" fontSize="2xl" my={10}>
            20. Contact Us
          </Heading>
          <Text>For questions about this Privacy Policy or our privacy practices, contact:</Text>
          <Text>
            <Text as="span" fontWeight="bold">
              Underline Science, Inc.
            </Text>
            <br />
            1216 Broadway, 2nd Floor
            <br />
            New York, NY 10001
            <br />
            United States
          </Text>
          <Text>
            Email:{' '}
            <Text as="span" fontWeight="bold">
              support@underline.io
            </Text>
          </Text>
        </Content>
      </Layout>
    </Fragment>
  );
};

export default PrivacyPolicy;
